/**
 * How many units of the session's cart belong to the store the page shows?
 *
 * The storefront header badge asks this. Carts are keyed (account, store);
 * the local `state.cart` is whichever store's cart a page last loaded, so the
 * server answers for the store the page shows (`Account.store_cart_count`,
 * api/src/models/account.py).
 *
 * A background read: its failure is the caller's to render (no toast), and
 * its 401 is not a verdict on the session (no retry, logout or redirect) --
 * the page's own requests own that.
 */
import {
  SKIP_AUTH_RECOVERY,
  SUPPRESS_ERROR_TOAST,
} from "@/interceptors/authResponseInterceptor";

/**
 * @param {object} axios     The app axios instance (`this.$axios`).
 * @param {number} accountId The account whose cart the session holds.
 * @param {number|string} storeId The store id the session gate resolved.
 * @returns {Promise<number>} The store's unit count. Rejects on a failed call
 *   or an answer that is not a non-negative integer.
 */
export async function fetchStoreCartCount(axios, accountId, storeId) {
  const response = await axios.post(
    `/accounts/${accountId}/store_cart_count`,
    { meta: { method: "store_cart_count", args: { store_id: Number(storeId) } } },
    { [SUPPRESS_ERROR_TOAST]: true, [SKIP_AUTH_RECOVERY]: true }
  );
  const count = response?.data?.meta?.result?.count;
  if (!Number.isInteger(count) || count < 0) {
    throw new Error("store_cart_count returned no usable count");
  }
  return count;
}
